'use client';

import { signInWithApple } from '@repo/auth';
import { Button, toast } from '@repo/ui';
import { Apple, Loader2 } from 'lucide-react';
import * as React from 'react';

const isAppleDevice = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
};

interface IAppleLoginButtonProps {
  onStart?: () => void;
}

export function AppleLoginButton({ onStart }: IAppleLoginButtonProps): React.ReactNode {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isApple, setIsApple] = React.useState(false);

  React.useEffect(() => {
    setIsApple(isAppleDevice());
  }, []);

  if (!isApple) {
    return null;
  }

  const handleAppleLogin = (): void => {
    onStart?.();
    setIsLoading(true);

    const signInPromise = signInWithApple().then(
      (response: { error?: { message?: string; status?: number; code?: string } }) => {
        if (response.error) {
          const error = new Error(response.error.message || 'Unknown error');
          (error as Error & { code?: string; status?: number }).code = response.error.code;
          (error as Error & { code?: string; status?: number }).status = response.error.status;
          throw error;
        }
        return response;
      },
    );

    toast.promise(signInPromise, {
      loading: 'Redirecting to Apple...',
      success: 'Redirecting...',
      error: (err: Error & { code?: string; status?: number }) => {
        setIsLoading(false);
        const details = [err.code, err.status ? `Status: ${err.status}` : null]
          .filter(Boolean)
          .join(' - ');
        return `Failed to sign in with Apple${details ? ` (${details})` : ''}: ${err.message}`;
      },
    });
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      className="w-full max-w-xs gap-2 bg-black text-white hover:bg-black/90 hover:text-white border-black"
      onClick={handleAppleLogin}
      disabled={isLoading}
    >
      {isLoading ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <Apple className="size-4 shrink-0" aria-hidden="true" />
      )}
      Sign in with Apple
    </Button>
  );
}
